import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ServiceProvider } from './contexts/ServiceContext';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import SkipLink from './components/common/SkipLink';
import ProtectedRoute from './components/common/ProtectedRoute';

// Lazy load page components
const HomePage = React.lazy(() => import('./pages/HomePage'));
const AITraining = React.lazy(() => import('./pages/services/AITraining'));
const ProcessAutomation = React.lazy(() => import('./pages/services/ProcessAutomation'));
const DigitalSolutions = React.lazy(() => import('./pages/services/DigitalSolutions'));
const ChatbotDevelopment = React.lazy(() => import('./pages/services/ChatbotDevelopment'));
const DataAnalytics = React.lazy(() => import('./pages/services/DataAnalytics'));
const InnovationConsulting = React.lazy(() => import('./pages/services/InnovationConsulting'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const Career = React.lazy(() => import('./pages/Career'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminContacts = React.lazy(() => import('./pages/AdminContacts'));
const BlogPostEditor = React.lazy(() => import('./pages/BlogPostEditor'));
const JobPostingEditor = React.lazy(() => import('./pages/JobPostingEditor'));

// Scroll restoration component
const ScrollToTop: React.FC = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  return null;
};

// Webmail redirect component
const WebmailRedirect: React.FC = () => {
  useEffect(() => {
    window.location.href = 'https://gfra1003.siteground.eu/webmail/log-in';
  }, []);

  return null;
};

// Main app wrapper that includes providers
const AppWrapper: React.FC = () => {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <ServiceProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </AuthProvider>
        </ServiceProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

// Main app content with routing
const AppContent: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="bg-secondary-black min-h-screen text-secondary-cream transition-colors duration-300">
      <SkipLink />
      <ScrollToTop />
      {!isAdminRoute && <Header />}
      
      <main id="main-content" className="relative" tabIndex={-1}>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/services/ai-training" element={<AITraining />} />
            <Route path="/services/process-automation" element={<ProcessAutomation />} />
            <Route path="/services/digital-solutions" element={<DigitalSolutions />} />
            <Route path="/services/chatbots" element={<ChatbotDevelopment />} />
            <Route path="/services/analytics" element={<DataAnalytics />} />
            <Route path="/services/consulting" element={<InnovationConsulting />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/career" element={<Career />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute role="admin" />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="posts/new" element={<BlogPostEditor />} />
              <Route path="posts/:id" element={<BlogPostEditor />} />
              <Route path="jobs/new" element={<JobPostingEditor />} />
              <Route path="jobs/:id" element={<JobPostingEditor />} />
            </Route>

            {/* Webmail Redirect */}
            <Route path="/webmail" element={<WebmailRedirect />} />
          </Routes>
        </Suspense>
      </main>
      
      {!isAdminRoute && <Footer />}
    </div>
  );
};

export default AppWrapper;